<template>
  <div class="quiz-content">
    <div class="quiz-content__modal">
      <div class="quiz-content__ilu-wrapper">
        <div class="quiz-content__wrapper">
          <div class="quiz-content__wrapper-limited">
            <TitleBar
              :title="title"
              @finalTime="saveFinalTime"
              @close-modal="$emit('close-modal')"
            />
            <div class="quiz-content__content-wrapper" ref="quizcontent">
              <div class="quiz-content__content">
                <Question
                  v-if="!isQuizComplete"
                  class="quiz-content__question"
                  :currentQuestion="currentQuestion"
                  :totalQuestions="totalQuestions"
                  :currentPosition="currentPosition + 1"
                />
                <Answers
                  v-if="!isQuizComplete"
                  class="quiz-content__answers"
                  ref="answers"
                  v-bind="{
                    currentQuestion,
                    lastQuestion,
                    firstQuestion,
                    isLoading,
                    lastSavedDate,
                    showFinished,
                  }"
                  @answer-selected="commitAnswer"
                  @move-previous-question="movePreviousQuestion"
                />
                <Completed
                  v-if="isQuizComplete"
                  class="quiz-content__completed"
                  :isQuizPassed="isQuizPassed"
                  :finalTime="finalTime"
                  :nextVideo="nextMedia"
                  :finalScore="finalScore"
                  @restart-quiz="restartQuiz"
                  @close-modal="$emit('close-modal')"
                />
              </div>
              <div class="quiz-content__submit">
                <div class="quiz-content__bottom-links">
                  <span
                    class="quiz-content__bottom-links__report-issue"
                    @click="startIssue"
                  >
                    Report an issue
                  </span>
                  <span
                    v-if="
                      isQuizComplete && this.nextMedia && this.nextMedia.uuid
                    "
                    @click="playNextVideo"
                    class="quiz-content__bottom-links__play-next-video"
                  >
                    Play next video
                    <RightCaretIcon
                      class="quiz-content__bottom-links__play-next-video--icon"
                    />
                  </span>
                  <button
                    v-if="!isQuizComplete"
                    @click="$emit('close-modal')"
                    class="quiz-content__exit__btn btn btn--secondary"
                  >
                    EXIT / CLOSE
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ProgressBar
          class="quiz-content__progress-bar"
          :value="percentComplete"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { highlightAllUnder } from 'prismjs'
import RightCaretIcon from '@/assets/icons/navigation/right_caret.svg'
import ProgressBar from '@/components/properties/ProgressBar.vue'
import TitleBar from './quiz-modal/TitleBar.vue'
import Question from './quiz-modal/Question.vue'
import Answers from './quiz-modal/Answers.vue'
import Completed from './quiz-modal/Completed.vue'

import 'prismjs/components/prism-python'

export default {
  name: 'QuizModal',
  components: {
    TitleBar,
    Question,
    Answers,
    Completed,
    ProgressBar,
    RightCaretIcon: { ...RightCaretIcon, name: 'RightCaretIcon' },
  },
  data() {
    return {
      currentPosition: 0,
      selectedAnswers: [],
      hasAnsweredAllQuestions: false,
      isQuizComplete: false,
      isQuizPassed: false,
      finalTime: null,
      finalScore: {},
      isLoading: false,
      lastSavedDate: null,
      showFinished: true,
    }
  },
  props: {
    attempt: {
      type: Object,
      required: true,
    },
    quiz: {
      type: Object,
      required: true,
    },
    finishedQuiz: {
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    nextMedia: {
      type: Object,
      required: false,
    },
    canStartIssue: {
      type: Boolean,
      required: false,
      default: () => {
        return true
      },
    },
    parentType: {
      type: String,
      required: false,
      default: () => 'isolated',
    },
    parent: {
      type: Object,
      required: false,
    },
  },
  computed: {
    questions() {
      const { questions } = this.attempt.questions
      return questions
    },
    totalQuestions() {
      return this.questions.length
    },
    currentQuestion() {
      return this.questions[this.currentPosition]
    },
    lastQuestion() {
      return this.questions[this.questions.length - 1]
    },
    firstQuestion() {
      return this.questions[0]
    },
    percentComplete() {
      if (this.finalTime) {
        return '100'
      }
      return Math.floor(
        ((this.currentPosition + 1) / this.totalQuestions) * 100
      ).toString()
    },
  },
  methods: {
    setSelectedAnswers(answer_ids) {
      const { question_id } = this.currentQuestion
      this.selectedAnswers = [
        ...this.selectedAnswers,
        { question_id, answers: answer_ids },
      ]
    },
    commitAnswer(answer_ids) {
      this.setSelectedAnswers(answer_ids)
      this.setNextQuestion()
    },
    async commitAnswerAndSubmit() {
      this.setSelectedAnswers(this.$refs.answers.currentSelection)
      await this.submitAttempt()
    },
    movePreviousQuestion(answer_ids) {
      this.setSelectedAnswers(answer_ids)
      this.setPreviousQuestion()
    },
    async setNextQuestion() {
      if (this.currentPosition === this.totalQuestions - 1) {
        this.hasAnsweredAllQuestions = true
        this.submitAttempt()
      } else {
        this.currentPosition = this.currentPosition + 1
        await this.submitPartialAttempt()
      }
      this.updatePrism()
    },
    async setPreviousQuestion() {
      if (this.currentPosition !== 0) {
        this.currentPosition = this.currentPosition - 1
        await this.submitPartialAttempt()
      }
      this.updatePrism()
    },
    saveFinalTime(time) {
      this.finalTime = time
    },
    async submitPartialAttempt() {
      this.isLoading = true
      const submission = this.generateSubmission()
      const payload = {
        quizId: this.quiz.id,
        attemptId: this.attempt.id,
        submission,
        status: 'pending',
      }
      try {
        await this.$store.dispatch('quizzes/updateQuizAttempt', payload)
        this.lastSavedDate = new Date()
      } catch (error) {
        this.$emit('quiz-error')
      }
      this.isLoading = false
    },
    async submitAttempt() {
      this.isLoading = true
      const submission = this.generateSubmission()
      const payload = {
        quizId: this.quiz.id,
        attemptId: this.attempt.id,
        submission,
      }
      await this.$store.dispatch('quizzes/finishQuizAttempt', payload)
      if (this.parentType === 'course') {
        await this.$store.dispatch('courses/getCourseById', this.parent.id)
      }
      await this.$store.dispatch('content/getQuizStatus', this.quiz.id)
      this.scoreQuiz()
      this.isLoading = false
      if (this.parentType === 'course') {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_QUIZ_FINISHED, {
          course_id: this.parent.id,
          course_name: this.parent.name,
          quiz_id: this.quiz.id,
          ...this.quiz,
        })
      }
    },
    generateSubmission() {
      const { questions, selectedAnswers } = this
      const submission = questions
      submission.map(question => {
        selectedAnswers.map(userAnswer => {
          if (userAnswer.question_id === question.question_id) {
            const { answers } = question
            answers.map(answer => {
              answer.is_selected_by_user = userAnswer.answers.includes(
                answer.answer_id
              )
            })
          }
        })
      })
      return submission
    },
    scoreQuiz() {
      const correct = []
      const incorrect = []
      const { questions } = this.finishedQuiz.questions

      questions.map(question => {
        const { answers } = question
        const totalCorrectAnswers = answers.filter(answer => answer.is_correct)
        const totalUserCorrectAnswers = answers.filter(
          answer => answer.is_selected_by_user && answer.is_correct
        )
        const totalUserIncorrectAnswers = answers.filter(
          answer => answer.is_selected_by_user && !answer.is_correct
        )

        if (
          totalCorrectAnswers.length === totalUserCorrectAnswers.length &&
          (!totalUserIncorrectAnswers || !totalUserIncorrectAnswers.length)
        ) {
          correct.push(question)
        } else {
          incorrect.push(question)
        }
      })

      this.finalScore = { correct, incorrect, questions }
      this.emitHeartbeat()
      if (this.finishedQuiz.score >= this.quiz.min_score) {
        this.isQuizPassed = true
      }
      this.isQuizComplete = true
    },
    emitHeartbeat() {
      const {
        finalTime: time_elapsed,
        finishedQuiz: { score },
      } = this
      this.$emit('heartbeat', { score, time_elapsed })
    },
    async restartQuiz() {
      this.isLoading = true
      await this.$store.dispatch('quizzes/restartQuiz')
      await this.$store.dispatch(
        'quizzes/startQuizAttemptByQuizId',
        this.quiz.id
      )
      this.currentPosition = 0
      this.selectedAnswers = []
      this.finalScore = {}
      this.finalTime = null
      this.isQuizPassed = false
      this.hasAnsweredAllQuestions = false
      this.isQuizComplete = false
      this.isLoading = false
    },
    updatePrism() {
      this.$nextTick().then(() => {
        if (this.$refs.quizcontent) {
          highlightAllUnder(this.$refs.quizcontent)
          if (window.MathJax) {
            window.MathJax.Hub.Config({
              tex2jax: {
                inlineMath: [['$', '$']],
                displayMath: [['$$', '$$']],
                processEscapes: true,
                processEnvironments: true,
              },
              // Center justify equations in code and markdown cells. Elsewhere
              // we use CSS to left justify single line equations in code cells.
              displayAlign: 'center',
              'HTML-CSS': {
                styles: { '.MathJax_Display': { margin: 0 } },
                linebreaks: { automatic: true },
              },
            })
            window.MathJax.Hub.Queue([
              'Typeset',
              window.MathJax.Hub,
              this.$refs.quizcontent,
            ])
          }
        }
      })
    },
    startIssue() {
      this.$emit('start-issue', {
        quiz_question_id: this.currentQuestion.question_id,
        quiz_attempt_id: this.attempt.id,
      })
    },
    playNextVideo() {
      const { uuid } = this.nextMedia
      this.$store.dispatch('content/setPlayingVideoId', uuid)
      this.$store.dispatch('content/setCurrentQuizId', null)
    },
  },
  created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
  },
  destroyed() {
    this.$store.dispatch('quizzes/clearQuizStore', '')
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
  watch: {
    currentQuestion: {
      immediate: true,
      deep: true,
      handler: 'updatePrism',
    },
  },
}
</script>

<style lang="scss">
.quiz-content {
  color: var(--white);
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--dark-gray) 0% 0% no-repeat padding-box;
    z-index: 205;
    overflow-y: scroll;
  }
  &__ilu-wrapper {
    position: relative;
    overflow: hidden;
    height: 100%;
    &:before {
      content: '';
      position: absolute;
      top: -500px;
      left: -600px;
      width: 840px;
      height: 1025px;
      transform: matrix(0, 1, -1, 0, 0, 0);
      background: transparent
        radial-gradient(closest-side at 50% 50%, #db28f2 0%, #8629f200 100%) 0%
        0% no-repeat padding-box;
      opacity: 0.14;
      z-index: -1;
    }
    &:after {
      content: '';
      position: absolute;
      bottom: -500px;
      right: -600px;
      width: 840px;
      height: 1025px;
      transform: matrix(0, 1, -1, 0, 0, 0);
      background: transparent
        radial-gradient(closest-side at 50% 50%, #db28f2 0%, #8629f200 100%) 0%
        0% no-repeat padding-box;
      opacity: 0.14;
      z-index: -1;
    }
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    z-index: 206;
    overflow: auto;
    position: relative;
    &-limited {
      max-width: 1400px;
      display: flex;
      flex-flow: row wrap;
      justify-content: space-around;
      align-content: flex-start;
      align-items: flex-start;
      margin: 0 auto;
    }
  }
  &__content-wrapper {
    flex: 1;
    margin: 30px;
    @include mobile {
      width: 85%;
      margin: 20px 20px 100px 50px;
    }
    position: relative;
  }
  &__content {
    display: flex;
    margin: 0 auto;
    flex-wrap: wrap;
    padding: 40px 40px 110px 40px;
    background: white;
    background: #222222 0% 0% no-repeat padding-box;
    box-shadow: 6px 0px 50px #0000001a;
    border-radius: 10px;
    position: relative;
    overflow: hidden;
    align-self: flex-start;
    @include desktop {
      flex-wrap: nowrap;
    }
    &:before {
      content: '';
      position: absolute;
      top: -400px;
      left: -400px;
      width: 667px;
      height: 711px;
      transform: matrix(0, 1, -1, 0, 0, 0);
      background: transparent
        radial-gradient(closest-side at 50% 50%, #215bc7 0%, #2113b100 100%) 0%
        0% no-repeat padding-box;
      opacity: 0.28;
    }
  }
  &__submit {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    margin: 0 0 30px 0;
  }
  &__bottom-links {
    width: 100%;
    padding: 5px 0;
    color: var(--silver-gray);
    text-decoration: underline;
    display: flex;
    align-items: center;
    margin: 20px 0;
    span {
      cursor: pointer;
      transition: color 0.3s ease;
      &:hover {
        color: var(--white);
        svg {
          fill: var(--white);
        }
      }
    }
    &__play-next-video {
      margin-left: auto;
      display: flex;
      align-items: center;
      text-decoration: none;
      text-transform: uppercase;
      svg {
        margin-left: 10px;
        fill: var(--silver-gray);
        transition: fill 0.3s ease;
      }
    }
  }
  &__exit__btn {
    margin-left: auto;
    margin-right: 0;
  }
  &__question {
    width: 100%;
    z-index: 1;
    @include desktop {
      width: 40%;
    }
  }
  &__answers {
    width: 100%;
    z-index: 1;
    padding-bottom: 60px;
    @include desktop {
      width: 60%;
      padding-bottom: 100px;
    }
  }
  &__completed {
    width: 100%;
  }
  .progress-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    padding: 0;
    z-index: 250;
    &__wrapper {
      border-radius: 0;
      padding: 0;
      margin: 0;
      background-color: transparent;
      text-align: center;
      width: 100%;
    }
    &__bar {
      height: 10px;
      &:after {
        display: none;
      }
    }
  }
  &__loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
  &__loader {
    width: 5rem;
  }
}
</style>
